/**
 * @module pages/MitarbeiterPulseDetail
 * @description Einzel-Pulse-Detailseite: Radar-Chart, Score-Cards, SOLL/IST-Tabelle,
 * Tagesverlauf, Arbeitszeit, Komplexitaets-Mix, AI-Report.
 * @since v2.161.0
 */
import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { Activity, Sparkles, ChevronUp, ChevronDown, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import BackLink from '../components/BackLink';

const API = import.meta.env.VITE_API_URL || '';
const CHART_COLORS = ['#338edc', '#22D3EE', '#6366F1', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6', '#EC4899', '#6B7280', '#D1D5DB'];

function rd1(n) { return n != null ? Number(n).toFixed(1).replace('.', ',') : '–'; }
function rd2(n) { return n != null ? Number(n).toFixed(2).replace('.', ',') : '–'; }

export default function MitarbeiterPulseDetail() {
  const { id } = useParams();
  const { t, locale } = useLanguage();
  const [zeitraum, setZeitraum] = useState('monat');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamAvg, setTeamAvg] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [sortCol, setSortCol] = useState('ratio');
  const [sortDir, setSortDir] = useState('asc');

  // Fetch pulse data
  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/mitarbeiter/${id}/pulse?zeitraum=${zeitraum}`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${API}/api/mitarbeiter/pulse/ranking?zeitraum=${zeitraum}`, { credentials: 'include' }).then(r => r.json()),
    ])
      .then(([pulse, ranking]) => {
        if (!pulse.error) setData(pulse);
        if (!ranking.error) setTeamAvg(ranking.team_avg);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, zeitraum]);

  // Generate AI report
  async function handleGenerateReport() {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/mitarbeiter/${id}/pulse/report`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ zeitraum }),
      });
      const result = await res.json();
      if (!result.error) {
        setData(prev => ({
          ...prev,
          ai_report: result.report?.ai_report || result.ai_report,
          letzter_report: result.report?.erstellt_am || new Date().toISOString(),
        }));
      }
    } catch {}
    setGenerating(false);
  }

  const scores = data?.scores || {};
  const ma = data?.mitarbeiter || {};
  const teamScores = teamAvg || {};

  // Radar data
  const radarData = useMemo(() => {
    const keys = ['geschwindigkeit', 'volumen', 'effizienz', 'konsistenz', 'komplexitaet'];
    const labels = {
      geschwindigkeit: t('maPulse.scores.geschwindigkeit'),
      volumen: t('maPulse.scores.volumen'),
      effizienz: t('maPulse.scores.effizienz'),
      konsistenz: t('maPulse.scores.konsistenz'),
      komplexitaet: t('maPulse.scores.komplexitaet'),
    };
    return keys.map(k => ({
      subject: labels[k],
      value: scores[k] || 0,
      team: teamScores[k] || 0,
    }));
  }, [scores, teamScores, t]);

  // Sorted SOLL/IST details
  const sollIstSorted = useMemo(() => {
    const details = data?.effizienz?.soll_ist_details || [];
    const list = [...details];
    list.sort((a, b) => {
      const va = a[sortCol] ?? 0;
      const vb = b[sortCol] ?? 0;
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return list;
  }, [data, sortCol, sortDir]);

  // Daily chart data
  const dailyData = useMemo(() => {
    return (data?.konsistenz?.tages_werte || []).map(d => ({
      datum: new Date(d.datum).toLocaleDateString(locale === 'de' ? 'de-AT' : 'en-US', { day: '2-digit', month: '2-digit' }),
      pakete: d.pakete,
      avg_min: d.avg_min,
      stunden: d.stunden,
      artikel: d.artikel,
    }));
  }, [data, locale]);

  // Complexity donut
  const donutData = useMemo(() => {
    return (data?.komplexitaet?.kunden_mix || []).slice(0, 8).map(k => ({
      name: k.kunde.length > 25 ? k.kunde.slice(0, 22) + '...' : k.kunde,
      value: k.anteil_pct,
    }));
  }, [data]);

  // SOLL reference for daily chart
  const sollAvg = data?.geschwindigkeit?.pack?.soll_avg_min;

  const zeitraumOptions = [
    { key: 'woche', label: t('maPulse.zeitraum.woche') },
    { key: 'monat', label: t('maPulse.zeitraum.monat') },
    { key: 'quartal', label: t('maPulse.zeitraum.quartal') },
  ];

  const aiReport = data?.ai_report;

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="py-20 text-center text-sm" style={{ color: 'var(--text-tertiary)' }}>
          {t('maPulse.loading')}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 md:p-8">
        <BackLink to="/mitarbeiter/pulse" label={t('maPulse.backToOverview')} className="mb-4" />
        <div className="py-20 text-center text-sm" style={{ color: 'var(--text-tertiary)' }}>
          {t('maPulse.noData')}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6">
      <BackLink to="/mitarbeiter/pulse" label={t('maPulse.backToOverview')} />
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>
            {ma.name}
          </h1>
          <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
            {ma.rolle === 'picker' ? 'Picker' : ma.rolle === 'packer' ? 'Packer' : 'Pick & Pack'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* Zeitraum Toggle */}
          <div className="flex rounded-[10px] p-[3px] border" style={{ background: 'var(--bg-inset, #F8F9FC)', borderColor: 'var(--border-card, #E5E7EB)' }}>
            {zeitraumOptions.map(o => (
              <button
                key={o.key}
                onClick={() => setZeitraum(o.key)}
                className={`px-4 py-1.5 text-sm rounded-[8px] transition-all ${
                  zeitraum === o.key ? 'bg-white font-semibold shadow-sm' : 'font-normal'
                }`}
                style={{ color: zeitraum === o.key ? 'var(--text-primary)' : 'var(--text-secondary)' }}
              >
                {o.label}
              </button>
            ))}
          </div>
          {/* Generate Report */}
          <button
            onClick={handleGenerateReport}
            disabled={generating}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-[10px] text-white transition-colors"
            style={{ background: generating ? '#9CA3AF' : '#338edc' }}
          >
            <Sparkles size={16} />
            {generating ? t('maPulse.generating') : t('maPulse.generateReport')}
          </button>
        </div>
      </div>

      {/* Radar + Gesamt Score */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Radar Chart */}
        <div className="border rounded-2xl p-6" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-card, #fff)' }}>
          <h3 className="text-sm font-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
            {t('maPulse.radarTitle')}
          </h3>
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                <PolarGrid stroke="var(--border-table, #F3F4F6)" />
                <PolarAngleAxis
                  dataKey="subject"
                  tick={{ fontSize: 12, fill: 'var(--text-secondary, #4B5563)' }}
                />
                <PolarRadiusAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 10, fill: 'var(--text-tertiary, #6B7280)' }}
                  axisLine={false}
                />
                <Radar name={t('maPulse.radarTeam')} dataKey="team" stroke="#9CA3AF" fill="#9CA3AF" fillOpacity={0.1} strokeDasharray="4 4" />
                <Radar name={ma.name || t('maPulse.mitarbeiterLabel')} dataKey="value" stroke="#338edc" fill="#338edc" fillOpacity={0.15} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center justify-center gap-6 mt-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-0.5 rounded" style={{ background: '#338edc' }} /> {ma.name?.split(' ')[0] || t('maPulse.mitarbeiterLabel')}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-0.5 rounded" style={{ background: '#9CA3AF', borderBottom: '1px dashed #9CA3AF' }} /> {t('maPulse.radarTeam')}
            </span>
          </div>
        </div>

        {/* Gesamt Score Card */}
        <div className="border rounded-2xl p-6" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-card, #fff)' }}>
          <h3 className="text-sm font-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
            {t('maPulse.scores.gesamt')}
          </h3>
          <div className="text-center mb-6">
            <div className="text-[48px] font-bold leading-none" style={{ color: 'var(--text-primary)' }}>
              {scores.gesamt ?? '–'}
            </div>
            <div className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>/ 100</div>
            {data?.geschwindigkeit?.pack?.team_rank && (
              <span
                className="inline-block mt-2 px-3 py-1 text-xs font-medium rounded-full"
                style={{ background: 'var(--bg-inset, #F8F9FC)', color: 'var(--text-secondary)' }}
              >
                {t('maPulse.rank', { rank: data.geschwindigkeit.pack.team_rank, total: data.geschwindigkeit.pack.team_total })}
              </span>
            )}
          </div>
          {/* Score Bars */}
          <div className="space-y-3">
            {[
              { key: 'geschwindigkeit', label: t('maPulse.scores.geschwindigkeit') },
              { key: 'volumen', label: t('maPulse.scores.volumen') },
              { key: 'effizienz', label: t('maPulse.scores.effizienz') },
              { key: 'konsistenz', label: t('maPulse.scores.konsistenz') },
              { key: 'komplexitaet', label: t('maPulse.scores.komplexitaet') },
            ].map(s => {
              const val = scores[s.key] || 0;
              return (
                <div key={s.key} className="flex items-center gap-3">
                  <span className="w-28 text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{s.label}</span>
                  <div className="flex-1 h-2.5 rounded-full" style={{ background: 'var(--border-card, #E5E7EB)' }}>
                    <div className="h-full rounded-full transition-all" style={{ width: `${val}%`, background: '#338edc' }} />
                  </div>
                  <span className="w-8 text-right text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{val}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* AI Report Card */}
      <div className="border rounded-2xl p-6" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-card, #fff)' }}>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={16} style={{ color: 'var(--text-secondary)' }} />
          <h3 className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
            {t('maPulse.aiReport')}
          </h3>
          {data?.letzter_report && (
            <span className="text-xs ml-2" style={{ color: 'var(--text-tertiary)' }}>
              {new Date(data.letzter_report).toLocaleDateString(locale === 'de' ? 'de-AT' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>

        {generating ? (
          <div className="py-8 text-center">
            <div className="inline-block w-5 h-5 border-2 border-t-transparent rounded-full animate-spin mb-2" style={{ borderColor: '#338edc', borderTopColor: 'transparent' }} />
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{t('maPulse.generating')}</p>
          </div>
        ) : aiReport ? (
          <div className="space-y-4">
            {/* Zusammenfassung */}
            {aiReport.zusammenfassung && (
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                {aiReport.zusammenfassung}
              </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Staerken */}
              {aiReport.staerken?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#10B981' }}>
                    {t('maPulse.staerken')}
                  </h4>
                  <ul className="space-y-1">
                    {aiReport.staerken.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-primary)' }}>
                        <span style={{ color: '#10B981' }}>+</span> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Verbesserung */}
              {aiReport.verbesserung?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#F59E0B' }}>
                    {t('maPulse.verbesserungsbedarf')}
                  </h4>
                  <ul className="space-y-1">
                    {aiReport.verbesserung.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-primary)' }}>
                        <span style={{ color: '#F59E0B' }}>!</span> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Trend */}
            {aiReport.trend && (
              <div className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-primary)' }}>
                <TrendingUp size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: 2 }} />
                {aiReport.trend}
              </div>
            )}

            {/* Empfehlung */}
            {aiReport.empfehlung && (
              <div
                className="rounded-xl p-4 border text-sm"
                style={{ background: 'var(--bg-inset, #F8F9FC)', borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-primary)' }}
              >
                <span className="text-xs font-semibold uppercase tracking-wider block mb-1" style={{ color: 'var(--text-secondary)' }}>
                  {t('maPulse.empfehlung')}
                </span>
                {aiReport.empfehlung}
              </div>
            )}
          </div>
        ) : (
          <p className="py-6 text-center text-sm" style={{ color: 'var(--text-tertiary)' }}>
            {t('maPulse.noReport')}
          </p>
        )}
      </div>

      {/* SOLL/IST Detail Table */}
      {(data?.effizienz?.soll_ist_details?.length > 0) && (
        <div className="border rounded-2xl overflow-hidden" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-card, #fff)' }}>
          <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-table, #F3F4F6)' }}>
            <h3 className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              {t('maPulse.sollIstVergleich')}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--border-table, #F3F4F6)' }}>
                  {[
                    { col: 'kunde', label: t('maPulse.kunde') },
                    { col: 'auftraege', label: t('maPulse.auftraege') },
                    { col: 'soll_avg', label: `${t('maPulse.sollAvg')} (min)` },
                    { col: 'ist_avg', label: `${t('maPulse.istAvg')} (min)` },
                    { col: 'ratio', label: t('maPulse.ratio') },
                  ].map(h => (
                    <th
                      key={h.col}
                      className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider cursor-pointer hover:text-[var(--text-primary)]"
                      style={{ color: 'var(--text-tertiary)' }}
                      onClick={() => {
                        if (sortCol === h.col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                        else { setSortCol(h.col); setSortDir(h.col === 'ratio' ? 'asc' : 'desc'); }
                      }}
                    >
                      <span className="inline-flex items-center gap-1">
                        {h.label}
                        {sortCol === h.col && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sollIstSorted.map((r, i) => (
                  <tr
                    key={i}
                    className="border-b last:border-0"
                    style={{ borderColor: 'var(--border-table, #F3F4F6)' }}
                  >
                    <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>{r.kunde}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>{r.auftraege}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>{rd2(r.soll_avg)}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>{rd2(r.ist_avg)}</td>
                    <td className="px-4 py-3">
                      <RatioBadge ratio={r.ratio} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tagesverlauf Chart */}
      {dailyData.length > 1 && (
        <div className="border rounded-2xl p-6" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-card, #fff)' }}>
          <h3 className="text-sm font-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
            {t('maPulse.tagesverlauf')}
          </h3>
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-table, #F3F4F6)" />
                <XAxis
                  dataKey="datum"
                  tick={{ fontSize: 12, fill: 'var(--text-tertiary, #6B7280)' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 12, fill: 'var(--text-tertiary, #6B7280)' }}
                  axisLine={false}
                  tickLine={false}
                  label={{ value: t('maPulse.paketeLabel'), angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: 'var(--text-tertiary)' } }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 12, fill: 'var(--text-tertiary, #6B7280)' }}
                  axisLine={false}
                  tickLine={false}
                  label={{ value: 'min/Pkt', angle: 90, position: 'insideRight', style: { fontSize: 11, fill: 'var(--text-tertiary)' } }}
                />
                <Tooltip
                  contentStyle={{
                    background: 'var(--bg-card, #fff)',
                    border: '1px solid var(--border-card, #E5E7EB)',
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                />
                <Bar yAxisId="left" dataKey="pakete" fill="#22D3EE" radius={[4, 4, 0, 0]} name={t('maPulse.paketeLabel')} />
                <Line yAxisId="right" type="monotone" dataKey="avg_min" stroke="#338edc" strokeWidth={2} dot={{ r: 3, fill: '#338edc' }} name="Ø min/Pkt" />
                {sollAvg && (
                  <ReferenceLine yAxisId="right" y={sollAvg} stroke="#9CA3AF" strokeDasharray="4 4" label={{ value: 'SOLL', position: 'right', style: { fontSize: 11, fill: '#9CA3AF' } }} />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Arbeitszeit + Komplexitaet */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Arbeitszeit */}
        <div className="border rounded-2xl p-6" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-card, #fff)' }}>
          <h3 className="text-sm font-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
            {t('maPulse.arbeitszeit')}
          </h3>
          <div className="space-y-3">
            <InfoRow label={t('maPulse.sollStunden')} value={`${data?.arbeitszeit?.soll_stunden ?? '–'}h`} />
            <InfoRow label={t('maPulse.istStunden')} value={`${data?.arbeitszeit?.ist_stunden ?? '–'}h`} />
            {(data?.arbeitszeit?.ueberstunden > 0) && (
              <InfoRow label={t('maPulse.ueberstunden')} value={`+${data.arbeitszeit.ueberstunden}h`} highlight="amber" />
            )}
            {(data?.arbeitszeit?.unterzeit > 0) && (
              <InfoRow label={t('maPulse.unterzeit')} value={`-${data.arbeitszeit.unterzeit}h`} highlight="red" />
            )}
            <div className="border-t pt-3 mt-3" style={{ borderColor: 'var(--border-table, #F3F4F6)' }}>
              <InfoRow label={t('maPulse.avgStart')} value={data?.arbeitszeit?.avg_start_uhrzeit || '–'} />
              <InfoRow label={t('maPulse.avgEnde')} value={data?.arbeitszeit?.avg_ende_uhrzeit || '–'} />
            </div>
          </div>
        </div>

        {/* Komplexitaets-Mix */}
        <div className="border rounded-2xl p-6" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-card, #fff)' }}>
          <h3 className="text-sm font-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
            {t('maPulse.komplexitaetsMix')}
          </h3>
          {donutData.length > 0 ? (
            <>
              <div style={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={donutData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius="55%"
                      outerRadius="80%"
                      paddingAngle={2}
                    >
                      {donutData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: 'var(--bg-card, #fff)',
                        border: '1px solid var(--border-card, #E5E7EB)',
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={(val) => [`${rd1(val)}%`]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                {data?.komplexitaet?.sop_anteil_pct > 0 && (
                  <span
                    className="px-2.5 py-0.5 text-xs font-medium rounded-full"
                    style={{ background: 'var(--bg-inset, #F8F9FC)', color: 'var(--text-secondary)' }}
                  >
                    SOP: {rd1(data.komplexitaet.sop_anteil_pct)}%
                  </span>
                )}
                <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                  Ø {rd1(data?.komplexitaet?.avg_artikel_pro_auftrag)} {t('maPulse.artikelProAuftrag')} (Team: {rd1(data?.komplexitaet?.team_avg_artikel)})
                </span>
              </div>
            </>
          ) : (
            <p className="py-6 text-center text-sm" style={{ color: 'var(--text-tertiary)' }}>{t('maPulse.noData')}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, highlight }) {
  let valueColor = 'var(--text-primary)';
  if (highlight === 'amber') valueColor = '#F59E0B';
  if (highlight === 'red') valueColor = '#EF4444';
  return (
    <div className="flex justify-between items-center py-1">
      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <span className="text-sm font-medium" style={{ color: valueColor }}>{value}</span>
    </div>
  );
}

function RatioBadge({ ratio }) {
  if (ratio == null) return <span style={{ color: 'var(--text-tertiary)' }}>–</span>;
  let bg, color;
  if (ratio >= 0.95) { bg = '#ECFDF5'; color = '#059669'; }
  else if (ratio >= 0.80) { bg = '#FFFBEB'; color = '#D97706'; }
  else { bg = '#FEF2F2'; color = '#DC2626'; }
  return (
    <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full" style={{ background: bg, color }}>
      {(ratio * 100).toFixed(0)}%
    </span>
  );
}
